import { isExists } from 'date-fns';
import type {
  ActiveFilterModelValue,
  DateMinMax,
  DateRangeFilterModelValue,
  Filter,
  FiltersForTable,
  MultipleSelectModelValue,
  SelectFilterOptions,
  SingleSelectModelValue,
  TextSearchOption,
} from '../../store';
import { FilterTypes, getSelectOptionKey } from './filters-utils';

export type FilterReadiness = 'ready' | 'pending';

type QueryValue = string | null;

type QueryRecord = Record<string, QueryValue | QueryValue[]>;

type OwnedQueryOptions = {
  searchFieldName?: string;
  paramPrefix?: string;
  syncTextSearch?: boolean;
};

type SerializeQueryOptions = OwnedQueryOptions & {
  pendingRawValues?: ReadonlyMap<string, string>;
  searchByOptions?: TextSearchOption[];
};

type ParseResult = { ok: true; value: ActiveFilterModelValue } | { ok: false };

type DateParts = { year: number; month: number; day: number };

type DateBoundsCarrier = DateMinMax & {
  startDate?: DateMinMax;
  endDate?: DateMinMax;
};

const NEEDLE_BY_QUERY_KEY = 'needleBy';
const PARAM_PREFIX_SEPARATOR = '.';
const MULTIPLE_VALUE_SEPARATOR = ',';
const DATE_RANGE_SEPARATOR = '..';
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const PARSE_FAILURE: ParseResult = { ok: false };
const PARSE_CLEARED: ParseResult = { ok: true, value: undefined };

const dedupe = (values: string[]): string[] => Array.from(new Set(values));

const withPrefix = (key: string, paramPrefix?: string): string =>
  paramPrefix ? `${paramPrefix}${PARAM_PREFIX_SEPARATOR}${key}` : key;

const getFilterOptions = (filter: Filter): SelectFilterOptions =>
  'options' in filter && Array.isArray(filter.options) ? filter.options : [];

const findOptionByKey = (
  options: SelectFilterOptions,
  key: string,
): SingleSelectModelValue | undefined =>
  options.find((option) => getSelectOptionKey(option?.value) === key);

const getDateBounds = (filter: Filter): DateBoundsCarrier =>
  filter as DateBoundsCarrier;

const toDateParts = (value: string | Date): DateParts | undefined => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return undefined;
    }
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }

  const isoMatch = ISO_DATE_PATTERN.exec(trimmed);
  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2]),
      day: Number(isoMatch[3]),
    };
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
  };
};

const formatDateParts = ({ year, month, day }: DateParts): string =>
  `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
    day,
  ).padStart(2, '0')}`;

const serializeDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string' && !(value instanceof Date)) {
    return undefined;
  }
  const parts = toDateParts(value);
  return parts ? formatDateParts(parts) : undefined;
};

const parseIsoDate = (raw: string): Date | undefined => {
  const match = ISO_DATE_PATTERN.exec(raw);
  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!isExists(year, month - 1, day)) {
    return undefined;
  }
  return new Date(year, month - 1, day);
};

const isWithinBounds = (isoDate: string, bounds?: DateMinMax): boolean => {
  const min = serializeDate(bounds?.min);
  const max = serializeDate(bounds?.max);

  if (min && isoDate < min) {
    return false;
  }
  if (max && isoDate > max) {
    return false;
  }
  return true;
};

export const isSyncableFilter = (filter: Filter): boolean =>
  filter.filterType !== FilterTypes.SELECT_PAGINATED &&
  filter.filterType !== FilterTypes.TEXT_SEARCH;

export const resolveSearchFieldName = (
  filtersConfig: Filter[] | undefined,
  searchFieldName?: string,
): string =>
  searchFieldName ||
  filtersConfig?.find((filter) => filter.filterType === FilterTypes.TEXT_SEARCH)
    ?.filterFieldName ||
  FilterTypes.TEXT_SEARCH;

const normalizeRawValue = (
  raw: QueryValue | QueryValue[] | undefined,
): string | undefined => {
  const first = Array.isArray(raw) ? raw[0] : raw;
  return typeof first === 'string' ? first : undefined;
};

export const isClosedSetFilter = (filter: Filter): boolean =>
  filter.filterType === FilterTypes.SELECT ||
  filter.filterType === FilterTypes.SELECT_MULTIPLE;

/*
  A closed set with no options is not "out of set" but not yet decidable:
  telling the two cases apart is what keeps a legitimate value from being
  dropped while the options are still in flight. When the page declares
  optionsLoaded, that flag decides: a load that ends with no options (API
  error, no locations) makes the filter decidable instead of holding it
  forever.
*/
export const getFilterReadiness = (filter: Filter): FilterReadiness => {
  if (!isClosedSetFilter(filter)) {
    return 'ready';
  }
  if ('optionsLoaded' in filter && filter.optionsLoaded !== undefined) {
    return filter.optionsLoaded ? 'ready' : 'pending';
  }
  return getFilterOptions(filter).length === 0 ? 'pending' : 'ready';
};

export const getQueryKey = (key: string, paramPrefix?: string): string =>
  withPrefix(key, paramPrefix);

export const getNeedleByQueryKey = (paramPrefix?: string): string =>
  withPrefix(NEEDLE_BY_QUERY_KEY, paramPrefix);

export const getFilterQueryKey = (
  filter: Filter,
  paramPrefix?: string,
): string => withPrefix(filter.filterFieldName, paramPrefix);

/*
  Search terms are often personal data (names, phone numbers, emails), and a
  query param reaches the browser history and the Sentry and Clarity traces:
  the search keys are claimed only when the page opts in.
*/
const claimsSearchKeys = ({ syncTextSearch }: OwnedQueryOptions): boolean =>
  syncTextSearch === true;

export const getOwnedQueryKeys = (
  filtersConfig: Filter[] | undefined,
  options: OwnedQueryOptions = {},
): string[] => {
  const { paramPrefix, searchFieldName } = options;

  const keys = (filtersConfig ?? [])
    .filter(isSyncableFilter)
    .map((filter) => getFilterQueryKey(filter, paramPrefix));

  if (claimsSearchKeys(options)) {
    keys.push(
      withPrefix(
        resolveSearchFieldName(filtersConfig, searchFieldName),
        paramPrefix,
      ),
      withPrefix(NEEDLE_BY_QUERY_KEY, paramPrefix),
    );
  }

  return dedupe(keys);
};

export const serializeFilterValue = (
  filter: Filter,
  value: ActiveFilterModelValue,
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  switch (filter.filterType) {
    case FilterTypes.TEXT_SEARCH:
    case FilterTypes.TYPED_TEXT: {
      if (typeof value !== 'string' && typeof value !== 'number') {
        return undefined;
      }
      const text = String(value).trim();
      return text === '' ? undefined : text;
    }

    case FilterTypes.SELECT: {
      const option = value as SingleSelectModelValue;
      if (typeof option !== 'object') {
        return undefined;
      }
      const key = getSelectOptionKey(option.value);
      return key ? key : undefined;
    }

    case FilterTypes.SELECT_MULTIPLE: {
      if (!Array.isArray(value)) {
        return undefined;
      }
      const keys = dedupe(
        (value as MultipleSelectModelValue)
          .map((option) => getSelectOptionKey(option?.value))
          .filter((key): key is string => Boolean(key)),
      );
      return keys.length === 0
        ? undefined
        : keys.join(MULTIPLE_VALUE_SEPARATOR);
    }

    case FilterTypes.DATE:
      return serializeDate(value);

    /*
      A range travels on a single `a..b` key: fromKey/toKey describe the fields
      the API expects and belong to transformFiltersToPayload, not to the shape
      of the URL, which must stay readable and atomic to validate.
    */
    case FilterTypes.DATE_RANGE: {
      const range = value as DateRangeFilterModelValue;
      if (typeof range !== 'object') {
        return undefined;
      }
      const start = serializeDate(range.startDate) ?? '';
      const end = serializeDate(range.endDate) ?? '';
      return start === '' && end === ''
        ? undefined
        : `${start}${DATE_RANGE_SEPARATOR}${end}`;
    }

    case FilterTypes.SELECT_PAGINATED:
      return undefined;
  }
};

const parseDateRangeValue = (filter: Filter, raw: string): ParseResult => {
  const separatorIndex = raw.indexOf(DATE_RANGE_SEPARATOR);
  if (separatorIndex < 0) {
    return PARSE_FAILURE;
  }

  const rawStart = raw.slice(0, separatorIndex).trim();
  const rawEnd = raw.slice(separatorIndex + DATE_RANGE_SEPARATOR.length).trim();

  if (rawEnd.includes(DATE_RANGE_SEPARATOR)) {
    return PARSE_FAILURE;
  }
  if (rawStart === '' && rawEnd === '') {
    return PARSE_CLEARED;
  }

  const bounds = getDateBounds(filter);
  let startDate: Date | undefined;
  let endDate: Date | undefined;

  if (rawStart !== '') {
    startDate = parseIsoDate(rawStart);
    if (!startDate || !isWithinBounds(rawStart, bounds.startDate)) {
      return PARSE_FAILURE;
    }
  }

  if (rawEnd !== '') {
    endDate = parseIsoDate(rawEnd);
    if (!endDate || !isWithinBounds(rawEnd, bounds.endDate)) {
      return PARSE_FAILURE;
    }
  }

  if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
    return PARSE_FAILURE;
  }

  return { ok: true, value: { startDate, endDate } };
};

export const parseFilterValue = (
  filter: Filter,
  raw: QueryValue | QueryValue[] | undefined,
): ParseResult => {
  if (filter.filterType === FilterTypes.SELECT_PAGINATED) {
    return PARSE_FAILURE;
  }

  const rawValue = normalizeRawValue(raw);
  if (rawValue === undefined) {
    return PARSE_FAILURE;
  }

  const trimmed = rawValue.trim();
  if (trimmed === '') {
    return PARSE_CLEARED;
  }

  switch (filter.filterType) {
    case FilterTypes.TEXT_SEARCH:
    case FilterTypes.TYPED_TEXT:
      return { ok: true, value: trimmed };

    case FilterTypes.SELECT: {
      const option = findOptionByKey(getFilterOptions(filter), trimmed);
      return option ? { ok: true, value: { ...option } } : PARSE_FAILURE;
    }

    /*
      An option with an array value (`['A','B']`) has the key `A,B`, which in
      the URL cannot be told apart from two tokens: at each position the
      longest token sequence that forms a key wins, so that selection survives
      a reload even when combined with other options. A match never spans more
      tokens than the longest option key, which keeps the parse linear.
    */
    case FilterTypes.SELECT_MULTIPLE: {
      const optionsByKey = new Map<string, SingleSelectModelValue>();
      let maxArity = 1;
      for (const option of getFilterOptions(filter)) {
        const key = getSelectOptionKey(option?.value);
        if (!key || optionsByKey.has(key)) continue;
        optionsByKey.set(key, option);
        maxArity = Math.max(
          maxArity,
          key.split(MULTIPLE_VALUE_SEPARATOR).length,
        );
      }

      const tokens = trimmed
        .split(MULTIPLE_VALUE_SEPARATOR)
        .map((token) => token.trim());
      const matched: MultipleSelectModelValue = [];
      const seen = new Set<string>();

      let start = 0;
      while (start < tokens.length) {
        let end = Math.min(tokens.length, start + maxArity);
        let key = '';
        for (; end > start; end -= 1) {
          key = tokens.slice(start, end).join(MULTIPLE_VALUE_SEPARATOR);
          if (optionsByKey.has(key)) break;
        }

        if (end === start) {
          start += 1;
          continue;
        }

        if (!seen.has(key)) {
          seen.add(key);
          matched.push({
            ...(optionsByKey.get(key) as SingleSelectModelValue),
          });
        }
        start = end;
      }

      return matched.length > 0 ? { ok: true, value: matched } : PARSE_FAILURE;
    }

    case FilterTypes.DATE: {
      const date = parseIsoDate(trimmed);
      if (!date) {
        return PARSE_FAILURE;
      }
      return isWithinBounds(trimmed, getDateBounds(filter))
        ? { ok: true, value: date }
        : PARSE_FAILURE;
    }

    case FilterTypes.DATE_RANGE:
      return parseDateRangeValue(filter, trimmed);

    default:
      return PARSE_FAILURE;
  }
};

export const getDefaultSearchByField = (
  searchByOptions: TextSearchOption[] | undefined,
): string | undefined =>
  (searchByOptions?.find((option) => option.default) ?? searchByOptions?.[0])
    ?.fieldName;

export const serializeFiltersToQuery = (
  filtersConfig: Filter[] | undefined,
  filtersForTable: FiltersForTable | undefined,
  options: SerializeQueryOptions = {},
): Record<string, string> => {
  const { paramPrefix, searchFieldName, pendingRawValues, searchByOptions } =
    options;
  const query: Record<string, string> = {};

  /*
    Params follow the order of the filters in the store, not the config order:
    it is the order in which the user added them, and whoever opens the link
    finds the chips in the same sequence.
  */
  const storeOrder = Object.keys(filtersForTable ?? {});
  const storeRank = (filter: Filter) => {
    const index = storeOrder.indexOf(filter.filterFieldName);
    return index === -1 ? storeOrder.length : index;
  };
  const orderedConfig = [...(filtersConfig ?? [])].sort(
    (a, b) => storeRank(a) - storeRank(b),
  );

  for (const filter of orderedConfig) {
    if (!isSyncableFilter(filter)) {
      continue;
    }

    const queryKey = getFilterQueryKey(filter, paramPrefix);
    const pendingRaw = pendingRawValues?.get(filter.filterFieldName);

    /*
      A held filter re-emits its raw value instead of dropping it: the
      projection then matches the current URL, and the canonical rewrite does
      not erase the deep link before it can be validated.
    */
    if (pendingRaw !== undefined) {
      if (pendingRaw !== '') {
        query[queryKey] = pendingRaw;
      }
      continue;
    }

    const entry = filtersForTable?.[filter.filterFieldName];
    if (!entry || entry.filterType !== filter.filterType) {
      continue;
    }

    const serialized = serializeFilterValue(filter, entry.value);
    if (serialized !== undefined) {
      query[queryKey] = serialized;
    }
  }

  if (claimsSearchKeys(options)) {
    const fieldName = resolveSearchFieldName(filtersConfig, searchFieldName);
    const entry = filtersForTable?.[fieldName];

    if (entry?.filterType === FilterTypes.TEXT_SEARCH) {
      const needle =
        typeof entry.value === 'string' ? entry.value.trim() : undefined;

      if (needle) {
        query[withPrefix(fieldName, paramPrefix)] = needle;

        const needleBy = entry.needleBy?.trim();
        const isAllowedNeedleBy =
          !searchByOptions ||
          searchByOptions.some((option) => option.fieldName === needleBy);
        if (needleBy && isAllowedNeedleBy) {
          query[withPrefix(NEEDLE_BY_QUERY_KEY, paramPrefix)] = needleBy;
        }
      }
    }
  }

  return query;
};

export const mergeOwnedQuery = (
  currentQuery: QueryRecord,
  ownedNext: Record<string, string>,
  ownedKeys: string[],
): QueryRecord => {
  const merged: QueryRecord = { ...currentQuery };

  for (const key of ownedKeys) {
    delete merged[key];
  }

  for (const [key, value] of Object.entries(ownedNext)) {
    if (value !== '') {
      merged[key] = value;
    }
  }

  return merged;
};

export const stringifyOwnedQuery = (owned: Record<string, string>): string =>
  Object.keys(owned)
    .filter((key) => Boolean(owned[key]))
    .sort()
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(owned[key])}`,
    )
    .join('&');
